import { sendEmail } from './emailServices';

/**
 * Send family invitation email using EmailJS
 * @param {Object} invitationData - Invitation data
 * @param {string} invitationData.invitedEmail - Email of the person being invited
 * @param {string} invitationData.inviterName - Name of the person sending the invitation
 * @param {string} invitationData.inviterEmail - Email of the person sending the invitation
 * @param {string} invitationData.familyName - Name of the family (optional)
 * @param {string} invitationData.invitationLink - The invitation link
 * @param {string} invitationData.expiresAt - When the invitation expires
 * @returns {Promise} - The email send result
 */
export const sendFamilyInvitationEmail = async (invitationData) => {
  try {
    console.log('📧 [InvitationEmail] Starting family invitation email...');
    console.log('📧 [InvitationEmail] Invitation data:', {
      invitedEmail: invitationData.invitedEmail,
      inviterName: invitationData.inviterName,
      invitationLink: invitationData.invitationLink
    });

    const emailParams = {
      mailOne: invitationData.invitedEmail,
      mailSubject: `${invitationData.inviterName} invited you to join their family finance account`,
      mailBody: getFamilyInvitationEmailTemplate(invitationData),
      // Additional template variables for fallback
      invited_email: invitationData.invitedEmail,
      inviter_name: invitationData.inviterName,
      inviter_email: invitationData.inviterEmail,
      family_name: invitationData.familyName || 'Family Finance',
      invitation_link: invitationData.invitationLink,
      expires_at: invitationData.expiresAt,
      company_name: 'Family Finance App',
      reply_to: invitationData.inviterEmail
    };

    console.log('📧 [InvitationEmail] Email parameters prepared:', {
      to: emailParams.mailOne,
      subject: emailParams.mailSubject,
      templateUsed: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'default'
    });

    const result = await sendEmail(emailParams, process.env.REACT_APP_EMAILJS_TEMPLATE_ID);
    
    if (result.success) {
      console.log('✅ [InvitationEmail] Family invitation email sent successfully!');
      console.log('📧 [InvitationEmail] EmailJS response:', result.result);
    } else {
      console.error('❌ [InvitationEmail] Family invitation email failed:', result.error);
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ [InvitationEmail] Invitation email error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate HTML email template for family invitation
 * @param {Object} data - Invitation data
 * @returns {string} - HTML email template
 */
const getFamilyInvitationEmailTemplate = (data) => {
  const inviterName = data.inviterName || 'Your family member';
  const familyName = data.familyName || 'Family Finance';
  const invitationLink = data.invitationLink || '#';
  const expiresAt = data.expiresAt || '7 days';
  const invitedEmail = data.invitedEmail || '';

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Family Finance Invitation</title>
    </head>
    <body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f8fafc;">
        <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
            
            <!-- Header -->
            <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 30px; text-align: center;">
                <div style="background-color: #ffffff; width: 80px; height: 80px; border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#667eea"/>
                        <path d="M19 15L20.09 18.26L24 19L20.09 19.74L19 23L17.91 19.74L14 19L17.91 18.26L19 15Z" fill="#764ba2"/>
                        <path d="M5 15L6.09 18.26L10 19L6.09 19.74L5 23L3.91 19.74L0 19L3.91 18.26L5 15Z" fill="#667eea"/>
                    </svg>
                </div>
                <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: 700; text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
                    You're Invited! 🎉
                </h1>
                <p style="color: #e2e8f0; margin: 10px 0 0 0; font-size: 16px; font-weight: 300;">
                    Join ${inviterName}'s family finance account
                </p>
            </div>

            <!-- Main Content -->
            <div style="padding: 40px 30px;">
                <div style="text-align: center; margin-bottom: 30px;">
                    <h2 style="color: #1a202c; margin: 0 0 15px 0; font-size: 24px; font-weight: 600;">
                        Welcome to ${familyName}!
                    </h2>
                    <p style="color: #4a5568; margin: 0; font-size: 16px; line-height: 1.6;">
                        ${inviterName} has invited you to join their family finance account. 
                        Together, you can manage budgets, track expenses, and achieve your financial goals.
                    </p>
                </div>

                <!-- Invitation Details -->
                <div style="background-color: #f7fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 25px; margin: 30px 0;">
                    <h3 style="color: #2d3748; margin: 0 0 15px 0; font-size: 18px; font-weight: 600;">
                        📋 Invitation Details
                    </h3>
                    <div style="display: flex; flex-direction: column; gap: 10px;">
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
                            <span style="color: #4a5568; font-weight: 500;">Invited by:</span>
                            <span style="color: #2d3748; font-weight: 600;">${inviterName}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
                            <span style="color: #4a5568; font-weight: 500;">Your email:</span>
                            <span style="color: #2d3748; font-weight: 600;">${invitedEmail}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0;">
                            <span style="color: #4a5568; font-weight: 500;">Expires in:</span>
                            <span style="color: #e53e3e; font-weight: 600;">${expiresAt}</span>
                        </div>
                    </div>
                </div>

                <!-- CTA Button -->
                <div style="text-align: center; margin: 35px 0;">
                    <a href="${invitationLink}" 
                       style="display: inline-block; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #ffffff; text-decoration: none; padding: 16px 32px; border-radius: 8px; font-weight: 600; font-size: 16px; box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4); transition: all 0.3s ease;">
                        Accept Invitation & Join Family
                    </a>
                </div>

                <!-- Features -->
                <div style="margin: 35px 0;">
                    <h3 style="color: #2d3748; margin: 0 0 20px 0; font-size: 18px; font-weight: 600; text-align: center;">
                        🚀 What you'll get access to:
                    </h3>
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
                        <div style="background-color: #f0fff4; border: 1px solid #9ae6b4; border-radius: 8px; padding: 15px; text-align: center;">
                            <div style="font-size: 24px; margin-bottom: 8px;">💰</div>
                            <h4 style="color: #22543d; margin: 0 0 5px 0; font-size: 14px; font-weight: 600;">Shared Budgets</h4>
                            <p style="color: #2f855a; margin: 0; font-size: 12px;">Track expenses together</p>
                        </div>
                        <div style="background-color: #ebf8ff; border: 1px solid #90cdf4; border-radius: 8px; padding: 15px; text-align: center;">
                            <div style="font-size: 24px; margin-bottom: 8px;">📊</div>
                            <h4 style="color: #2a4365; margin: 0 0 5px 0; font-size: 14px; font-weight: 600;">Analytics</h4>
                            <p style="color: #3182ce; margin: 0; font-size: 12px;">Insights & reports</p>
                        </div>
                        <div style="background-color: #faf5ff; border: 1px solid #c6adff; border-radius: 8px; padding: 15px; text-align: center;">
                            <div style="font-size: 24px; margin-bottom: 8px;">🔔</div>
                            <h4 style="color: #553c9a; margin: 0 0 5px 0; font-size: 14px; font-weight: 600;">Alerts</h4>
                            <p style="color: #805ad5; margin: 0; font-size: 12px;">Budget notifications</p>
                        </div>
                        <div style="background-color: #fff5f5; border: 1px solid #feb2b2; border-radius: 8px; padding: 15px; text-align: center;">
                            <div style="font-size: 24px; margin-bottom: 8px;">🎯</div>
                            <h4 style="color: #742a2a; margin: 0 0 5px 0; font-size: 14px; font-weight: 600;">Goals</h4>
                            <p style="color: #e53e3e; margin: 0; font-size: 12px;">Financial targets</p>
                        </div>
                    </div>
                </div>

                <!-- Security Note -->
                <div style="background-color: #fffaf0; border: 1px solid #fbd38d; border-radius: 8px; padding: 20px; margin: 30px 0;">
                    <div style="display: flex; align-items: flex-start; gap: 12px;">
                        <div style="font-size: 20px; color: #dd6b20;">🔒</div>
                        <div>
                            <h4 style="color: #c05621; margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">
                                Your Privacy & Security
                            </h4>
                            <p style="color: #9c4221; margin: 0; font-size: 13px; line-height: 1.5;">
                                This invitation is secure and encrypted. Only you can accept this invitation using this email address. 
                                If you didn't expect this invitation, you can safely ignore this email.
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Footer Message -->
                <div style="text-align: center; margin-top: 40px; padding-top: 30px; border-top: 1px solid #e2e8f0;">
                    <p style="color: #718096; margin: 0 0 10px 0; font-size: 14px;">
                        Questions? Reply to this email or contact ${inviterName} directly.
                    </p>
                    <p style="color: #a0aec0; margin: 0; font-size: 12px;">
                        This invitation will expire in ${expiresAt}. Don't wait too long!
                    </p>
                </div>
            </div>

            <!-- Footer -->
            <div style="background-color: #2d3748; color: #a0aec0; padding: 25px 30px; text-align: center;">
                <p style="margin: 0 0 10px 0; font-size: 14px; font-weight: 500;">
                    Family Finance App
                </p>
                <p style="margin: 0; font-size: 12px;">
                    Making family finances simple and transparent
                </p>
            </div>
        </div>
    </body>
    </html>
  `;
};
